import { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../config/firebase';
import { Trip } from '../types/trip';

/**
 * Subscribes to the full trip object at trips/{tripId} (meta + members +
 * itinerary + travel + expenses + settlements + lodging).
 *
 * Subscribes once per tripId change, cleans up on unmount. Does NOT
 * re-subscribe on every render.
 *
 * Scale note: With 100+ itinerary items or expenses, the full-trip payload
 * grows. For React Native or offline-first, consider splitting into
 * per-tab subscriptions (useItinerary, useExpenses) so tabs load on demand
 * instead of all upfront.
 */
export function useTrip(tripId: string) {
  const [data, setData] = useState<Trip | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!tripId) {
      setIsLoading(false);
      return;
    }
    const tripRef = ref(db, `trips/${tripId}`);
    const unsubscribe = onValue(
      tripRef,
      (snapshot) => {
        const val = snapshot.val();
        if (val && val.meta) {
          setData(val);
        } else {
          setData(null);
        }
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [tripId]);

  return { data, isLoading, error };
}
